import React, { ChangeEvent, ChangeEventHandler, useMemo } from 'react';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { TariffViewModel } from '@common/model/billing/tariff/view';
import { MOBILE_BREAKPOINT_THRESHOLD, styled } from '@cp/ds/src/theme';
import { useIsMobile, useTheme } from '@cp/ds/src/hooks';
import { mergeSx } from '@cp/ds/src/utils/sx/merge';
import { TariffTokens } from './tariff';
import { TariffCard } from './tariffCard';

const OPTIONS_IN_ROW_COUNT = 3;

const Root = styled('fieldset', { name: 'TariffGrid' })(({ theme }) => ({
  marginInlineStart: 0,
  marginInlineEnd: 0,
  border: 0,
  padding: '2px',
  display: 'grid',
  gridTemplateColumns: `repeat(${OPTIONS_IN_ROW_COUNT}, 1fr)`,
  gap: theme.spacing(1.5),
  [TariffTokens.RootBG]: '#fff',
  [TariffTokens.BorderColorChecked]: theme.palette['brand gray'],
  [TariffTokens.Shadow]: '14px 17px 40px 4px rgba(138, 182, 209, 0.08)',
  [TariffTokens.ShadowChecked]: '0px 2.17px 3.25px 0px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.up(MOBILE_BREAKPOINT_THRESHOLD)]: {
    gap: theme.spacing(2.5),
  },
}));

export interface TariffSliderProps extends BoxProps {
  name: string;
  options: TariffViewModel[];
  value?: string | number;
  defaultValue?: string | number;
  promoLabel?: React.ReactNode;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onTariffSelect?: (value: TariffViewModel['id']) => void;
}

export const TariffSlider: React.FC<TariffSliderProps> = ({
  name,
  options = [],
  value,
  defaultValue,
  promoLabel,
  onChange,
  onTariffSelect,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const onOptionChange = onTariffSelect
    ? (e: ChangeEvent<HTMLInputElement>) => {
        onChange?.(e);
        if (e.currentTarget.checked) {
          onTariffSelect?.(Number(e.currentTarget.value));
        }
      }
    : onChange;
  const computedValue = String(value) || String(defaultValue);

  const selectedOptionIndex = options.findIndex((o) => String(o.id) === computedValue);
  const selectedOptionIndexInRow = selectedOptionIndex < 0 ? undefined : selectedOptionIndex % OPTIONS_IN_ROW_COUNT;
  const benefitLevels = useMemo(
    () => [
      {},
      {
        [TariffTokens.BenefitBG]: 'rgb(255, 241, 241)',
        [TariffTokens.BenefitFG]: theme.palette['brand pink hover'],
      },
      {
        [TariffTokens.BenefitBG]: theme.palette['gray light'],
        [TariffTokens.BenefitFG]: theme.palette['gray dark'],
      },
    ],
    [],
  );

  return (
    <Box
      {...props}
      sx={mergeSx(
        {
          position: 'relative',
          overflow: 'hidden',
        },
        props.sx,
      )}
    >
      <Root
        sx={
          isMobile
            ? {
                transition: 'transform 0.3s ease-out',
                ...(selectedOptionIndexInRow !== undefined && selectedOptionIndexInRow !== 1
                  ? selectedOptionIndexInRow === 0
                    ? // 0
                      {
                        transform: 'translateX(12px)',
                      }
                    : // 2
                      {
                        transform: 'translateX(calc(100vw - 100% - 12px))',
                      }
                  : // undefined || 1
                    {
                      transform: 'translateX(calc((100vw - 100%) / 2))',
                    }),
              }
            : undefined
        }
      >
        {options.map((tariff, i) => {
          const isSelected = String(tariff.id) === computedValue;
          return (
            <TariffCard
              baseTariff={options[0]}
              key={tariff.id}
              name={name}
              onChange={onOptionChange}
              selected={isSelected}
              sx={benefitLevels[i]}
              tariff={tariff}
            />
          );
        })}
      </Root>
    </Box>
  );
};
